import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import update from 'immutability-helper';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { connect } from 'react-redux';
import { orderBy, isArray, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { shortenStringWithTooltip } from '../helper';
import CustomTooltip from '../Tooltip/CustomTooltip';

const style = {
  padding: '0',
  marginBottom: '1rem',
  backgroundColor: 'white',
  cursor: 'pointer',
};

function DragDropSort({ isShow, isToggle, itemList, saveSequence, devOrderId, fromComp, productIdentifierList, numberOfImages, sampleId }) {
  const [cards, setCards] = useState([]);
  const { t } = useTranslation('common');
  useEffect(() => {
    if (Array.isArray(itemList) && itemList.length > 0 && fromComp !== 'POTemplate' && fromComp !== 'designDetailImages') {
      let item = [...itemList];
      item = orderBy(item, ['sequence'], ['asc']);
      setCards(item);
    } else if ((fromComp === 'POTemplate' || fromComp === 'designDetailImages') && Array.isArray(itemList) && itemList.length > 0) {
      setCards([...itemList]);
    }
  }, [itemList, fromComp]);

  const renderCard = (card, index) => {
    const cardId = card._id != null ? card._id : index;
    const uniqueKey = fromComp === 'designDetailImages' ? card?.image || card?._id || `img-${index}` : cardId;
    return (
      <List
        key={uniqueKey}
        index={index}
        id={cardId}
        item={card}
        moveCard={moveCard}
        fromComponent={fromComp}
        productIdentifierList={productIdentifierList}
      />
    );
  };

  const moveCard = (dragIndex, hoverIndex) => {
    const dragRecord = cards[dragIndex];
    setCards(
      update(cards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      }),
    );
  };

  const submitSequence = (e) => {
    const cardsToProcess = fromComp === 'designDetailImages' && numberOfImages ? cards.slice(0, numberOfImages) : cards;
    const updatedSequence = cardsToProcess.map((item, i) => {
      if (fromComp === 'sampleImages') {
        // keep _id (original set index) so backend can reorder by index
        return { _id: item._id, sampleId: item.sampleId };
      }
      if (fromComp === 'sampleImagesItems' || fromComp === 'supplierImagesItems') {
        delete item._id;
        return item;
      }
      if (fromComp === 'designDeatails') {
        return {
          ...item,
          _id: item._id,
          sequence: i + 1,
        };
      }
      if (fromComp === 'POTemplate') {
        return { ...item, sequence: i + 1 };
      }
      if (fromComp === 'customModule') {
        return { ...item, sequence: i + 1 };
      }
      if (fromComp === 'customModuleGroup') {
        return { ...item, sequence: i + 1 };
      }
      if (fromComp === 'careProducts') {
        return { ...item, sequence: i + 1 };
      }
      if (fromComp === 'careLabelProducts' || fromComp === 'careUserInstructionsGroup') {
        return { ...item, sequence: i + 1 };
      }
      if (fromComp === 'sampleAnnotations') {
        delete item._id;
        return { ...item, sequence: i + 1 };
      }
      if (fromComp === 'libraryBomType') {
        return { _id: item._id, name: item?.name, sequence: i + 1 };
      }
      if (fromComp === 'designDetailImages') {
        return { ...item };
      }
      if (fromComp === 'sampleSpecPom') {
        return { ...item, sequence: i + 1 };
      }
      return {
        _id: item._id,
        sequence: i + 1,
        code: item?.code,
      };
    });

    let document = {};

    if (fromComp === 'sampleImages') {
      document = {
        devOederId: devOrderId,
        libraryType: 'sampleImages',
        sequence: [...updatedSequence],
        type: 'sampleImages',
      };
    } else if (fromComp === 'sampleImagesItems') {
      document = {
        devOederId: devOrderId,
        libraryType: 'sampleImagesItems',
        sequence: [...updatedSequence],
        type: 'sampleImagesItems',
      };
    } else if (fromComp === 'supplierImagesItems') {
      document = {
        devOederId: devOrderId,
        libraryType: 'supplierImagesItems',
        sequence: [...updatedSequence],
        type: 'supplierImagesItems',
      };
    } else if (fromComp === 'designDeatails') {
      document = {
        devOederId: devOrderId,
        libraryType: 'designDeatails',
        sequence: [...updatedSequence],
        type: 'designDeatails',
      };
    } else if (fromComp === 'productTesting') {
      document = {
        devOederId: devOrderId,
        libraryType: 'testing',
        sequence: [...updatedSequence],
        type: 'testing-requirements',
      };
    } else if (fromComp === 'POTemplate') {
      document = {
        libraryType: 'po_template',
        sequence: [...updatedSequence],
      };
    } else if (fromComp === 'customModule') {
      document = {
        libraryType: 'custom_module',
        sequence: [...updatedSequence],
      };
    } else if (fromComp === 'customModuleGroup') {
      document = {
        libraryType: 'custom_module_group',
        sequence: [...updatedSequence],
      };
    } else if (fromComp === 'careProducts') {
      document = {
        devOrderId,
        module: 'care',
        type: 'care',
        sequence: updatedSequence,
      };
    } else if (fromComp === 'careLabelProducts') {
      document = {
        devOrderId,
        module: 'care-label',
        sequence: updatedSequence,
      };
    } else if (fromComp === 'careUserInstructionsGroup') {
      document = {
        module: 'care',
        sequence: updatedSequence,
      };
    } else if (fromComp === 'sampleAnnotations') {
      document = {
        module: 'sampleAnnotations',
        sequence: [...updatedSequence],
      };
    } else if (fromComp === 'libraryBomType') {
      document = {
        module: 'libraryBomType',
        sequence: [...updatedSequence],
      };
    } else if (fromComp === 'designDetailImages') {
      let finalSequence = updatedSequence;

      if (numberOfImages && Array.isArray(itemList) && itemList.length > numberOfImages) {
        const remainingImages = itemList.slice(numberOfImages);
        finalSequence = [...updatedSequence, ...remainingImages];
      }

      document = {
        sequence: finalSequence,
      };
    } else if (fromComp === 'sampleSpecPom') {
      document = {
        sampleId,
        devOrderId,
        poms: updatedSequence.map((item) => ({ code: item.code, sequence: item.sequence })),
      };
    } else {
      document = {
        devOrderId,
        module: 'bom',
        type: 'bom-type',
        sequence: updatedSequence,
      };
    }

    saveSequence(e, document);
  };

  return (
    <Modal className="common-custom-modal rearrange-modal modal-medium right" show={isShow} onHide={isToggle}>
      <Modal.Header className="modal-header-custom">
        <Modal.Title>
          <span className="f-24">
            {fromComp === 'POTemplate' ? t('DRAG_DROP_SORT.REARRANGE_CARDS_MODAL_TITLE') : t('DRAG_DROP_SORT.REARRANGE_SEQUENCE_MODAL_TITLE')}
          </span>
        </Modal.Title>
        <span onClick={isToggle} className="icon-close cursor-pointer" />
      </Modal.Header>

      <Modal.Body className="rearrange-body">
        <DndProvider backend={HTML5Backend}>
          <div>{(fromComp === 'designDetailImages' && numberOfImages ? cards.slice(0, numberOfImages) : cards).map((card, i) => renderCard(card, i))}</div>
        </DndProvider>
      </Modal.Body>
      <Modal.Footer className="rearrange-footer">
        <div className="w-100 d-flex align-items-center m-0 justify-content-end">
          <button aria-label="save" type="button" className="btn p-0 line-normal" onClick={submitSequence}>
            <i className="icon-Tick-1 f-24 text-grey-color" />
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function List({ id, item, index, moveCard, fromComponent, productIdentifierList, imageUrl }) {
  const ref = useRef(null);
  const { t } = useTranslation('common');

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(ele, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = ele.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex, ele, index);
      ele.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const handleBom = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div className="ref-code">
          <label className="m-0 mr-3">
            <span className="rearrange-name">{t('DRAG_DROP_SORT.REFERENCE_CODE')}</span>{' '}
            <span>{item?.referenceCode ? shortenStringWithTooltip(item?.referenceCode, 7) : ''}</span>
          </label>

          <label className="m-0">
            <span className="rearrange-name">{t('DRAG_DROP_SORT.BOM_SUBTYPE')}</span>{' '}
            <span>{item?.bomSubType?.name ? shortenStringWithTooltip(item?.bomSubType?.name, 10) : ''}</span>
          </label>
        </div>
      </div>
    );
  };

  const handleCareProduct = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-drag_drop" />
        </div>
        <div className="ref-code">
          <label className="m-0 mr-3">
            <strong>{t('DRAG_DROP_SORT.CODE')}</strong> <span>{item?.code ? shortenStringWithTooltip(item?.code, 10) : ''}</span>
          </label>

          <label className="m-0">
            <strong>{t('DRAG_DROP_SORT.CARE_TYPE')}</strong> <span>{item?.careType ? shortenStringWithTooltip(item?.careType, 10) : ''}</span>
          </label>
        </div>
      </div>
    );
  };

  const handleCareLabelProduct = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div className="ref-code">
          <label className="m-0 mr-3">
            <strong>{t('DRAG_DROP_SORT.CODE')}</strong>
            <CustomTooltip tooltipText={item?.code}>
              <span>{item?.code ? shortenStringWithTooltip(item?.code, 10) : ''}</span>
            </CustomTooltip>
          </label>

          <label className="m-0">
            <strong>{t('DRAG_DROP_SORT.CARE_TYPE')}</strong>
            <CustomTooltip tooltipText={item?.careType?.name}>
              <span>{item?.careType?.name ? shortenStringWithTooltip(item?.careType?.name, 10) : ''}</span>
            </CustomTooltip>
          </label>
        </div>
      </div>
    );
  };

  const getTypeString = () => {
    if (!item) return '';
    if (isArray(item.type)) {
      return shortenStringWithTooltip(item?.type?.name.join(', '), 15);
    }
    if (item.type) {
      return shortenStringWithTooltip(item?.type?.name, 15);
    }
    return '';
  };

  const handleTest = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div className="ref-code">
          <label className="m-0 mr-3">
            <strong className="text-bold">{t('DRAG_DROP_SORT.TESTING_CODE')}</strong>{' '}
            <span className="text-book">{item?.code ? shortenStringWithTooltip(item?.code, 10) : ''}</span>
          </label>

          <label className="m-0">
            <strong className="text-bold">{t('DRAG_DROP_SORT.TEST_TYPE')}</strong> <span className="text-book">{getTypeString()}</span>
          </label>
        </div>
      </div>
    );
  };

  const handleAllControl = () => {
    if (item?.label === 'Google') {
      return item?.title || item?.label;
    }
    if (item?.label === 'Product Journey') {
      return item?.subHeaderTitle || item?.label;
    }
    if (item?.label === 'Testing Control') {
      return item?.testGroup?.[0]?.testGroupTitle || item?.label;
    }

    if (item?.label === 'Heading') {
      return item?.value || item?.label;
    }

    if (item?.label === 'Source') {
      if (item?.moduleName === 'productIdentifier') {
        return productIdentifierList?.filter((datas) => datas?._id === item?.relation?.subLocation)?.map((datas) => datas?.name);
      }
      return item?.label;
    }

    return item.label;
  };

  const handlePOCards = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div>
          <label className="m-0 mr-3 ml-2">
            <strong>
              <span>
                {item?.title ? shortenStringWithTooltip(item?.title, 50) : handleAllControl() ? shortenStringWithTooltip(handleAllControl(), 50) : ''}
              </span>
            </strong>
          </label>
        </div>
      </div>
    );
  };

  const handleGroupLibraryCards = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div>
          <label className="m-0 mr-3 ml-2">
            <span>{item?.moduleName ? shortenStringWithTooltip(item?.moduleName, 50) : ''}</span>
          </label>
        </div>
      </div>
    );
  };

  const handleSampleImages = () => {
    return (
      <div className="d-flex align-items-center design-detail-box ">
        <div className="d-flex">
          <div className="move-cursor">
            <i className="icon-Rearrange f-20" />
          </div>
        </div>
        <label key={index} className="px-2 m-0">
          <strong>{`Image Set ${id + 1}`}</strong>
        </label>
      </div>
    );
  };
  const handleSampleImagesItemsAndDesignDetails = () => {
    return (
      <div className="d-flex design-detail-box ">
        <div className="d-flex align-items-center">
          <div className="move-cursor">
            <i className="icon-Rearrange f-20" />
          </div>
          <div className="rearrange-text mb-0">
            <p className="f16 text-medium mb-0">{item?.name ? shortenStringWithTooltip(item?.name, 15) : ''}</p>
          </div>
        </div>
        <label className="m-0 ml-auto">
          <div className="img-preview-inside-table-in-dragDrop">
            <span className="big-image">
              {(item?.imageUrl || item?.image) && <img className="img-fluid" title={item?.name} alt={item?.name} src={item?.imageUrl || item?.image} />}
            </span>
            {(item?.imageUrl || item?.image) && (
              <div className="rearrange-img">
                <img title={item?.name} alt={item?.name} src={item?.imageUrl || item?.image} className="img-fluid" />{' '}
              </div>
            )}
          </div>
        </label>
      </div>
    );
  };

  const handleLibraryBomType = () => {
    return (
      <div className="d-flex design-detail-box ">
        <div className="d-flex align-items-center">
          <div className="move-cursor">
            <i className="icon-Rearrange f-20" />
          </div>
          <div className="rearrange-text d-flex align-items-center mb-0">
            <p className="f16 text-bold mb-0">BOM Type:&nbsp;</p>
            <p className="f16 text-medium mb-0">{item?.name ? shortenStringWithTooltip(item?.name, 15) : ''}</p>
          </div>
        </div>
        <label htmlFor={`${item?._id}`} className="m-0 ml-auto">
          <div className="img-preview-inside-table-in-dragDrop">
            <span className="big-image">
              {item?.image && <img className="img-fluid" title={item?.name} alt={`${item?.name}`} src={`${imageUrl}/${item?.image}`} />}
            </span>
            {item?.image && (
              <div className="rearrange-img">
                <img title={item?.name} alt={`${item?.name}`} src={`${imageUrl}/${item?.image}`} className="img-fluid" />{' '}
              </div>
            )}
          </div>
        </label>
      </div>
    );
  };
  const handleSampleAnnotation = () => {
    return (
      <div className="d-flex design-detail-box ">
        <div className="d-flex align-items-center">
          <div className="move-cursor">
            <i className="icon-Rearrange f-20" />
          </div>
          <div className="rearrange-text mb-0">
            <p className="f16 text-medium mb-0">{item?.name ? shortenStringWithTooltip(item?.name, 15) : ''}</p>
          </div>
        </div>
        <label className="m-0 ml-auto d-flex align-items-center">
          <div className="img-preview-inside-table-in-dragDrop">
            <span className="big-image">
              {(item?.imageUrl || item?.image) && (
                <img className="img-fluid" title={item?.name} alt={`${item?.name}`} src={item?.annotedImage || item?.image} />
              )}
            </span>
            {(item?.imageUrl || item?.image) && (
              <div className="rearrange-img">
                <img title={item?.name} alt={`${item?.name}`} src={item?.annotedImage || item?.imageUrl || item?.image} className="img-fluid" />{' '}
              </div>
            )}
          </div>
        </label>
      </div>
    );
  };

  const handleSampleSpecPom = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div className="ref-code">
          <label className="m-0 mr-3">
            <strong>Code: </strong>
            <span>{item?.code ? shortenStringWithTooltip(item.code, 10) : ''}</span>
          </label>
          <label className="m-0">
            <span>{item?.description ? shortenStringWithTooltip(item.description, 20) : ''}</span>
          </label>
        </div>
      </div>
    );
  };

  const handleDesignDetailImages = () => {
    const imageSrc = item?.annotedImage && !isEmpty(item?.annotedImage) ? item.annotedImage : item?.image;
    const displayImage =
      imageSrc?.startsWith('http://') || imageSrc?.startsWith('https://') || imageSrc?.startsWith('data:image/') ? imageSrc : `${imageUrl}/${imageSrc}`;

    const imageName = item?.imageTitle && item.imageTitle.trim() ? item.imageTitle.trim() : '';

    return (
      <div className="d-flex design-detail-box align-items-center">
        <div className="d-flex align-items-center flex-fill">
          <div className="move-cursor">
            <i className="icon-Rearrange f-20" />
          </div>
          {imageName && (
            <div className="rearrange-text mb-0 ml-2 flex-fill">
              <p className="f16 text-medium mb-0" title={imageName}>
                {shortenStringWithTooltip(imageName, 20)}
              </p>
            </div>
          )}
        </div>
        <div className="m-0 ml-auto d-flex align-items-center">
          <div className="img-preview-inside-table-in-dragDrop">
            <span className="big-image">{imageSrc && <img className="img-fluid" title={imageName || ''} alt={imageName || ''} src={displayImage} />}</span>
            {imageSrc && (
              <div className="rearrange-img">
                <img title={imageName || ''} alt={imageName || ''} src={displayImage} className="img-fluid" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleCustomlibrary = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div>
          <label className="m-0 mr-3 ml-2">
            <span>{item?.pageTitle ? shortenStringWithTooltip(item?.pageTitle, 50) : ''}</span>
          </label>
        </div>
      </div>
    );
  };

  const handleCareUserInstruction = () => {
    return (
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div className="ml-16">
          <label className="m-0">
            <span className="font-medium">{item?.tabName ? shortenStringWithTooltip(item?.tabName, 30) : ''}</span>
          </label>
        </div>
      </div>
    );
  };

  const modalToShow = () => {
    switch (fromComponent) {
      case 'POTemplate':
        return handlePOCards();
      case 'customModule':
        return handleCustomlibrary();
      case 'customModuleGroup':
        return handleGroupLibraryCards();
      case 'productTesting':
        return handleTest();
      case 'designDeatails':
        return handleSampleImagesItemsAndDesignDetails();
      case 'sampleImages':
        return handleSampleImages();
      case 'careProducts':
        return handleCareProduct();
      case 'careLabelProducts':
        return handleCareLabelProduct();
      case 'careUserInstructionsGroup':
        return handleCareUserInstruction();
      case 'sampleAnnotations':
        return handleSampleAnnotation();
      case 'designDetailImages':
        return handleDesignDetailImages();
      case 'libraryBomType':
        return handleLibraryBomType();
      case 'sampleSpecPom':
        return handleSampleSpecPom();
      default:
        return handleSampleImagesItemsAndDesignDetails();
    }
  };

  return (
    <div ref={ref} style={{ ...style, opacity }} data-handler-id={handlerId}>
      {fromComponent ? modalToShow() : handleBom()}
    </div>
  );
}

const mapStateToProps = ({ BOMConfigDetails }) => {
  const { tabs } = BOMConfigDetails;
  return {
    tabs,
  };
};

const mapStateToDispatch = {};

export default connect(mapStateToProps, mapStateToDispatch)(DragDropSort);
